// ─────────────────────────────────────────────────────────────────────────────
// RevenueCat bridge for the iOS Capacitor build.
//
// This module is a no-op on the web. When the app is running inside the
// Capacitor iOS shell it:
//   1. Configures the RevenueCat SDK with the public iOS key.
//   2. Logs the user in with their Bilateral Calm `user_id`, so RC events
//      arrive on our backend webhook tagged to the right account.
//   3. Tells the backend the RC App User ID via `/api/billing/link-revenuecat`
//      so subscription events can find the user.
//
// All purchase / restore / paywall calls live in this file too, so the rest
// of the app can stay platform-agnostic.
// ─────────────────────────────────────────────────────────────────────────────

import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PUBLIC_KEY = process.env.REACT_APP_REVENUECAT_IOS_PUBLIC_KEY;
const PREMIUM_ENT = process.env.REACT_APP_REVENUECAT_PREMIUM_ENTITLEMENT || "premium";

// Capacitor's `isNativePlatform()` returns true on iOS / Android, false in
// every browser context. We require it through a try/catch so the web build
// doesn't blow up when @capacitor/core resolves before the native shell exists.
const isNative = () => {
  try {
    // eslint-disable-next-line global-require
    const { Capacitor } = require("@capacitor/core");
    return Capacitor?.isNativePlatform?.() === true;
  } catch {
    return false;
  }
};

let configured = false;

/** Configure the SDK + log the user in. Safe to call repeatedly. */
export async function setupRevenueCat(userId) {
  if (!isNative() || !userId || !PUBLIC_KEY) return;

  // eslint-disable-next-line global-require
  const { Purchases, LOG_LEVEL } = require("@revenuecat/purchases-capacitor");

  if (!configured) {
    await Purchases.setLogLevel({ level: LOG_LEVEL.WARN });
    await Purchases.configure({ apiKey: PUBLIC_KEY, appUserID: userId });
    configured = true;
  } else {
    await Purchases.logIn({ appUserID: userId });
  }

  // Mirror the RC App User ID onto our user document so the webhook can
  // resolve incoming events. RC's App User ID == the value we passed into
  // `appUserID` above.
  try {
    await axios.post(
      `${API}/billing/link-revenuecat`,
      { revenuecat_app_user_id: userId },
      { withCredentials: true }
    );
  } catch (e) {
    // Non-fatal — webhook will still fall back to matching `user_id`.
  }
}

/** Read current entitlement directly from the SDK cache. iOS only. */
export async function getPremiumStatus() {
  if (!isNative()) return { active: false, expiresAt: null };
  // eslint-disable-next-line global-require
  const { Purchases } = require("@revenuecat/purchases-capacitor");
  const info = await Purchases.getCustomerInfo();
  const ent = info?.customerInfo?.entitlements?.active?.[PREMIUM_ENT];
  return {
    active: !!ent,
    expiresAt: ent?.expirationDate || null,
    productIdentifier: ent?.productIdentifier || null,
  };
}

/** Fetch the current Offering and return its `availablePackages`. iOS only. */
export async function getAvailablePackages() {
  if (!isNative()) return [];
  // eslint-disable-next-line global-require
  const { Purchases } = require("@revenuecat/purchases-capacitor");
  const offerings = await Purchases.getOfferings();
  return offerings?.current?.availablePackages || [];
}

/** Trigger StoreKit purchase for a package. iOS only. */
export async function purchasePackage(pkg) {
  if (!isNative()) throw new Error("revenuecat_not_native");
  // eslint-disable-next-line global-require
  const { Purchases } = require("@revenuecat/purchases-capacitor");
  return Purchases.purchasePackage({ aPackage: pkg });
}

/** Restore prior purchases (required by App Store guidelines). iOS only. */
export async function restorePurchases() {
  if (!isNative()) throw new Error("revenuecat_not_native");
  // eslint-disable-next-line global-require
  const { Purchases } = require("@revenuecat/purchases-capacitor");
  return Purchases.restorePurchases();
}

/** Logout — wipe RC alias, used when our app logs out. iOS only. */
export async function logoutRevenueCat() {
  if (!isNative() || !configured) return;
  // eslint-disable-next-line global-require
  const { Purchases } = require("@revenuecat/purchases-capacitor");
  try { await Purchases.logOut(); } catch { /* user wasn't aliased */ }
}

export const isNativeCapacitor = isNative;
